import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createCandidature, updateCandidatureStatus, deleteCandidature, getAllCandidatures, getUserCandidatures, getSortedCandidatures, searchCandidaturesByAppelOffreTitle, getCandidaturesByAppelOffreOwner } from '../services/apiService';

export const createCandidatures = createAsyncThunk(
  'candidatures/createCandidature',
  async (candidatureData, { rejectWithValue }) => {
    try {
      return await createCandidature(candidatureData);
    } catch (error) {
      const errorMessage =
        error.response &&
        error.response.data &&
        error.response.data.messages &&
        error.response.data.messages.value
          ? error.response.data.messages.value
          : "Vous avez une candidature en attente pour cette offre";
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateCandidatures = createAsyncThunk(
  'candidatures/updateCandidatureStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      return await updateCandidatureStatus(id, status);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteCandidatures = createAsyncThunk(
  'candidatures/deleteCandidature',
  async (id, { rejectWithValue }) => {
    try {
      await deleteCandidature(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchAllCandidatures = createAsyncThunk(
  'candidatures/fetchAllCandidatures',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllCandidatures();
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCurrentUserCandidatures = createAsyncThunk(
  'candidatures/fetchCurrentUserCandidatures',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUserCandidatures();
      return response.candidatures;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur inconnue");
    }
  }
);

export const fetchSortedCandidatures = createAsyncThunk(
  'candidatures/fetchSortedCandidatures',
  async ({ sort, order, status }, { rejectWithValue }) => {
    try {
      return await getSortedCandidatures({ sort, order, status }); // Pass status to the API call
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur inconnue");
    }
  }
);

export const searchCandidatures = createAsyncThunk(
  'candidatures/searchCandidatures',
  async (title, { rejectWithValue }) => {
    try {
      const response = await searchCandidaturesByAppelOffreTitle(title);
      return Array.isArray(response) ? response : []; // Ensure response is an array
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Erreur inconnue");
    }
  }
);

export const fetchCandidaturesByAppelOffreOwner = createAsyncThunk(
  "candidatures/fetchCandidaturesByAppelOffreOwner",
  async (_, { rejectWithValue }) => {
    try {
      return await getCandidaturesByAppelOffreOwner();
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur inconnue");
    }
  }
);

const candidatureSlice = createSlice({
  name: 'candidatures',
  initialState: {
    candidatures: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createCandidatures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCandidatures.fulfilled, (state, action) => {
        state.loading = false;
        state.candidatures.push(action.payload);
      })
      .addCase(createCandidatures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCandidatures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCandidatures.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.candidatures.findIndex(
          (candidature) => candidature.id === action.payload.candidature.id
        );
        if (index !== -1) {
          state.candidatures[index] = action.payload.candidature;
        }
      })
      .addCase(updateCandidatures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCandidatures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCandidatures.fulfilled, (state, action) => {
        state.loading = false;
        state.candidatures = state.candidatures.filter(
          (candidature) => candidature.id !== action.payload
        );
      })
      .addCase(deleteCandidatures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllCandidatures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCandidatures.fulfilled, (state, action) => {
        state.loading = false;
        state.candidatures = action.payload.candidatures;
      })
      .addCase(fetchAllCandidatures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCurrentUserCandidatures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUserCandidatures.fulfilled, (state, action) => {
        state.loading = false;
        state.candidatures = Array.isArray(action.payload) ? action.payload : []; // Ensure it's an array
      })
      .addCase(fetchCurrentUserCandidatures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSortedCandidatures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSortedCandidatures.fulfilled, (state, action) => {
        state.loading = false;
        state.candidatures = action.payload;
      })
      .addCase(fetchSortedCandidatures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(searchCandidatures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchCandidatures.fulfilled, (state, action) => {
        state.loading = false;
        state.candidatures = action.payload;
      })
      .addCase(searchCandidatures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCandidaturesByAppelOffreOwner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidaturesByAppelOffreOwner.fulfilled, (state, action) => {
        state.loading = false;
        state.candidatures = action.payload;
      })
      .addCase(fetchCandidaturesByAppelOffreOwner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default candidatureSlice.reducer;
